import { useState, useEffect, useCallback, useMemo } from "react";
import EmptyState from "./EmptyState";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");
const authFetch = async (path, opts = {}) => {
  const res  = await fetch(API + path, { ...opts, headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken(), ...opts.headers } });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Failed");
  return data;
};

const TABS = [
  { id:"tasks",      label:"✅ Tasks"         },
  { id:"kanban",     label:"🗂️ Kanban Board"  },
  { id:"team",       label:"👤 Team"           },
  { id:"automation", label:"⚡ Automation"     },
  { id:"whatsapp",   label:"💬 WhatsApp Alerts"},
  { id:"reports",    label:"📊 Reports"        },
];

const PRIORITIES = [
  { id:"urgent", label:"🔴 Urgent", color:"#dc2626", bg:"#fef2f2" },
  { id:"high",   label:"🟠 High",   color:"#d97706", bg:"#fffbeb" },
  { id:"normal", label:"🔵 Normal", color:"#2563eb", bg:"#eff6ff" },
  { id:"low",    label:"⚪ Low",    color:"#9ca3af", bg:"#f9fafb" },
];

const STATUSES = [
  { id:"todo",        label:"To Do",       color:"#6b7280", bg:"#f9fafb" },
  { id:"in_progress", label:"In Progress", color:"#2563eb", bg:"#eff6ff" },
  { id:"review",      label:"Review",      color:"#d97706", bg:"#fffbeb" },
  { id:"done",        label:"Done",        color:"#15803d", bg:"#f0fdf4" },
];

const CATEGORIES = ["Warehouse","Finance","Orders","Marketing","Catalog","Logistics","Customer","Platform","Admin","Other"];

const RULE_TRIGGERS = [
  { id:"low_stock",      label:"📦 Product goes low stock" },
  { id:"new_order",      label:"🛒 New order received" },
  { id:"daily_morning",  label:"🌅 Every day 9 AM" },
  { id:"weekly_monday",  label:"📅 Every Monday" },
  { id:"monthly_1st",    label:"🗓️ 1st of every month" },
  { id:"order_pending",  label:"⏳ Order pending > 24 hours" },
];

const RULE_ACTIONS = [
  { id:"create_task",    label:"✅ Create a task" },
  { id:"whatsapp_alert", label:"💬 Send WhatsApp alert" },
  { id:"email_alert",    label:"📧 Send email alert" },
];

const S = {
  card:   { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:12 },
  hdr:    { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", fontWeight:700, fontSize:13, display:"flex", alignItems:"center", justifyContent:"space-between" },
  th:     { padding:"7px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:"#9ca3af", background:"#f9fafb", whiteSpace:"nowrap" },
  td:     { padding:"7px 12px", fontSize:12, borderBottom:"1px solid #f3f4f6", verticalAlign:"middle" },
  btn:    (c="#F47920") => ({ padding:"7px 14px", borderRadius:7, fontSize:11, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut: { padding:"6px 12px", borderRadius:7, fontSize:11, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:  { padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  label:  { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  badge:  (c,bg) => ({ background:bg, color:c, padding:"2px 8px", borderRadius:10, fontSize:10, fontWeight:700, whiteSpace:"nowrap" }),
};

const EMPTY_TASK = { title:"", category:"Warehouse", priority:"normal", status:"todo", dueDate:"", assignee:"", description:"", checklist:[] };
const EMPTY_RULE = { name:"", trigger:"low_stock", action:"create_task", taskTitle:"", whatsappNo:"", active:true };

function PriorityBadge({ id }) {
  const p = PRIORITIES.find(x=>x.id===id) || PRIORITIES[2];
  return <span style={S.badge(p.color, p.bg)}>{p.label}</span>;
}

function StatusBadge({ id }) {
  const s = STATUSES.find(x=>x.id===id) || STATUSES[0];
  return <span style={S.badge(s.color, s.bg)}>{s.label}</span>;
}

function isOverdue(task) {
  return task.dueDate && task.status!=="done" && new Date(task.dueDate) < new Date();
}

function daysUntil(date) {
  if (!date) return null;
  const diff = Math.ceil((new Date(date)-Date.now())/86400000);
  return diff;
}

export default function PageTasksPro({ toast, tenant, orders=[], products=[] }) {
  const [tab,         setTab]        = useState("tasks");
  const [tasks,       setTasks]      = useState([]);
  const [loading,     setLoading]    = useState(true);
  const [showModal,   setShowModal]  = useState(false);
  const [editTask,    setEditTask]   = useState(null);
  const [form,        setForm]       = useState(EMPTY_TASK);
  const [search,      setSearch]     = useState("");
  const [filterCat,   setFilterCat]  = useState("all");
  const [filterPri,   setFilterPri]  = useState("all");
  const [filterStat,  setFilterStat] = useState("all");
  const [filterAss,   setFilterAss]  = useState("all");
  const [members,     setMembers]    = useState([]);
  const [rules,       setRules]      = useState(() => {
    try { return JSON.parse(localStorage.getItem("3june_rules")||"[]"); } catch { return []; }
  });
  const [showRuleForm,setShowRuleForm]= useState(false);
  const [ruleForm,    setRuleForm]   = useState(EMPTY_RULE);
  const [waNumber,    setWaNumber]   = useState(localStorage.getItem("3june_wa_number")||"");
  const [waSaved,     setWaSaved]    = useState(false);
  const [checkInput,  setCheckInput] = useState("");
  const [aiLoading,   setAiLoading]  = useState(false);

  // Load tasks
  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const data = await authFetch("/tasks");
      setTasks(data.tasks || []);
    } catch { setTasks([]); }
    setLoading(false);
  }, []);

  // Load team members
  const loadMembers = useCallback(async () => {
    try {
      const data = await authFetch("/team");
      setMembers(data.members || []);
    } catch { setMembers([]); }
  }, []);

  useEffect(() => { loadTasks(); loadMembers(); }, []);

  const setF = (k,v) => setForm(f=>({...f,[k]:v}));

  // Save task
  const saveTask = async () => {
    if (!form.title.trim()) { toast("Task title required","error"); return; }
    try {
      if (editTask) {
        await authFetch("/tasks/"+editTask._id, { method:"PUT", body:JSON.stringify(form) });
        toast("Task updated ✓","success");
      } else {
        await authFetch("/tasks", { method:"POST", body:JSON.stringify(form) });
        toast("Task created ✓","success");
      }
      setShowModal(false);
      setEditTask(null);
      setForm(EMPTY_TASK);
      loadTasks();
    } catch(e) { toast(e.message,"error"); }
  };

  const changeStatus = async (taskId, status) => {
    try {
      await authFetch("/tasks/"+taskId, { method:"PUT", body:JSON.stringify({ status }) });
      setTasks(prev=>prev.map(t=>t._id===taskId?{...t,status}:t));
    } catch(e) { toast(e.message,"error"); }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await authFetch("/tasks/"+taskId, { method:"DELETE" });
      setTasks(prev=>prev.filter(t=>t._id!==taskId));
      toast("Deleted","success");
    } catch(e) { toast(e.message,"error"); }
  };

  const openEdit = (t) => { setEditTask(t); setForm({ ...EMPTY_TASK, ...t, dueDate:t.dueDate?new Date(t.dueDate).toISOString().slice(0,10):"", checklist:t.checklist||[] }); setShowModal(true); };
  const openAdd  = (defaults={}) => { setEditTask(null); setForm({...EMPTY_TASK,...defaults}); setShowModal(true); };

  // Filter tasks
  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return tasks.filter(t => {
      const matchSearch = !q || t.title?.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q);
      const matchCat    = filterCat==="all"  || t.category===filterCat;
      const matchPri    = filterPri==="all"  || t.priority===filterPri;
      const matchStat   = filterStat==="all" || t.status===filterStat;
      const matchAss    = filterAss==="all"  || t.assignee===filterAss;
      return matchSearch && matchCat && matchPri && matchStat && matchAss;
    }).sort((a,b) => {
      const priOrder = { urgent:0, high:1, normal:2, low:3 };
      if (priOrder[a.priority] !== priOrder[b.priority]) return priOrder[a.priority]-priOrder[b.priority];
      if (a.dueDate && b.dueDate) return new Date(a.dueDate)-new Date(b.dueDate);
      return 0;
    });
  }, [tasks, search, filterCat, filterPri, filterStat, filterAss]);

  // Stats
  const totalTasks   = tasks.length;
  const todayTasks   = tasks.filter(t=>{ if(!t.dueDate) return false; return new Date(t.dueDate).toDateString()===new Date().toDateString(); });
  const overdueTasks = tasks.filter(isOverdue);
  const doneTasks    = tasks.filter(t=>t.status==="done");
  const urgentTasks  = tasks.filter(t=>t.priority==="urgent" && t.status!=="done");

  // AI task suggestions
  const generateTasks = async () => {
    setAiLoading(true);
    try {
      const lowStock  = products.filter(p=>Object.values(p.inventory||{}).reduce((a,b)=>a+b,0)<=(p.lowAlert||5));
      const pending   = orders.filter(o=>!o.fulfillment_status).length;
      const data = await authFetch("/ai/text", {
        method:"POST",
        body: JSON.stringify({
          prompt:`You are an operations manager for an Indian ecommerce leather belts brand.

Current situation:
- ${lowStock.length} products with low stock: ${lowStock.slice(0,5).map(p=>p.name).join(", ")}
- ${pending} pending orders waiting to be fulfilled
- Today is ${new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long"})}
- Total tasks currently: ${totalTasks}, Overdue: ${overdueTasks.length}

Generate 5 actionable tasks for today. Return as JSON array:
[{"title":"...","category":"...","priority":"urgent|high|normal|low","description":"...","dueDate":"${new Date().toISOString().slice(0,10)}"}]

Categories: ${CATEGORIES.join(", ")}
Return ONLY the JSON array.`,
          maxTokens:600,
        }),
      });
      const text      = (data.result||"").replace(/```json|```/g,"").trim();
      const suggested = JSON.parse(text);
      // Add all suggested tasks
      for (const t of suggested) {
        await authFetch("/tasks", { method:"POST", body:JSON.stringify({...EMPTY_TASK,...t}) });
      }
      await loadTasks();
      toast(`✅ ${suggested.length} tasks generated by AI`,"success");
    } catch(e) { toast("AI error: "+e.message,"error"); }
    setAiLoading(false);
  };

  // Save automation rule
  const saveRule = () => {
    if (!ruleForm.name) { toast("Rule name required","error"); return; }
    const updated = editTask?rules.map(r=>r.id===editTask?ruleForm:r):[{...ruleForm,id:Date.now()},...rules];
    setRules(updated);
    localStorage.setItem("3june_rules", JSON.stringify(updated));
    setShowRuleForm(false);
    setRuleForm(EMPTY_RULE);
    toast("Rule saved ✓","success");
  };

  const deleteRule = (id) => {
    const updated = rules.filter(r=>r.id!==id);
    setRules(updated);
    localStorage.setItem("3june_rules", JSON.stringify(updated));
  };

  const toggleRule = (id) => {
    const updated = rules.map(r=>r.id===id?{...r,active:!r.active}:r);
    setRules(updated);
    localStorage.setItem("3june_rules", JSON.stringify(updated));
  };

  const saveWaNumber = () => {
    localStorage.setItem("3june_wa_number", waNumber);
    setWaSaved(true);
    setTimeout(()=>setWaSaved(false), 2000);
    toast("WhatsApp number saved ✓","success");
  };

  // Kanban tasks by status
  const kanbanCols = STATUSES.map(s=>({ ...s, tasks:filtered.filter(t=>t.status===s.id) }));

  return (
    <div>
      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:16 }}>⚡ Task & Operations</div>
          <div style={{ fontSize:12, color:"#9ca3af" }}>Task planner, team, automation and WhatsApp alerts</div>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <button style={{ ...S.btn("#6366f1"), opacity:aiLoading?0.7:1 }} onClick={generateTasks} disabled={aiLoading}>
            {aiLoading?"🤖 Generating…":"🤖 AI Generate Tasks"}
          </button>
          <button style={S.btn()} onClick={()=>openAdd()}>+ New Task</button>
        </div>
      </div>

      {/* KPIs */}
      <div className="kpi-grid" style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:10, marginBottom:14 }}>
        {[
          { label:"Total Tasks",   value:totalTasks,        color:"#2563eb", icon:"📋" },
          { label:"Due Today",     value:todayTasks.length, color:"#F47920", icon:"📅" },
          { label:"Overdue",       value:overdueTasks.length,color:"#dc2626",icon:"⚠️" },
          { label:"Urgent",        value:urgentTasks.length, color:"#d97706", icon:"🔴" },
          { label:"Done",          value:doneTasks.length,   color:"#15803d", icon:"✅" },
        ].map(k=>(
          <div key={k.label} style={{ background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"11px 14px", borderLeft:"3px solid "+k.color, display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:20 }}>{k.icon}</span>
            <div>
              <div style={{ fontSize:9, color:"#9ca3af", fontWeight:700, textTransform:"uppercase" }}>{k.label}</div>
              <div style={{ fontSize:20, fontWeight:700, color:k.color }}>{k.value}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:14, overflowX:"auto" }}>
        {TABS.map(t=>(
          <button key={t.id} style={{ padding:"9px 16px", border:"none", borderBottom:tab===t.id?"2px solid #F47920":"2px solid transparent", background:"none", color:tab===t.id?"#F47920":"#6b7280", fontWeight:tab===t.id?600:400, fontSize:12, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" }}
            onClick={()=>setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ══ TASKS ══ */}
      {tab === "tasks" && (
        <div>
          {/* Filters */}
          <div style={{ display:"flex", gap:8, marginBottom:12, flexWrap:"wrap", alignItems:"center" }}>
            <input style={{ ...S.input, width:200 }} placeholder="🔍 Search tasks…" value={search} onChange={e=>setSearch(e.target.value)}/>
            <select style={{ ...S.input, width:"auto" }} value={filterCat} onChange={e=>setFilterCat(e.target.value)}>
              <option value="all">All Categories</option>
              {CATEGORIES.map(c=><option key={c}>{c}</option>)}
            </select>
            <select style={{ ...S.input, width:"auto" }} value={filterPri} onChange={e=>setFilterPri(e.target.value)}>
              <option value="all">All Priority</option>
              {PRIORITIES.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
            <select style={{ ...S.input, width:"auto" }} value={filterStat} onChange={e=>setFilterStat(e.target.value)}>
              <option value="all">All Status</option>
              {STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            {members.length>0 && (
              <select style={{ ...S.input, width:"auto" }} value={filterAss} onChange={e=>setFilterAss(e.target.value)}>
                <option value="all">All Assignees</option>
                {members.map(m=><option key={m._id} value={m.name}>{m.name}</option>)}
              </select>
            )}
            <span style={{ fontSize:11, color:"#9ca3af" }}>{filtered.length} tasks</span>
          </div>

          {loading ? (
            <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>Loading tasks…</div>
          ) : filtered.length===0 ? (
            <div style={{ ...S.card, padding:20 }}>
              <EmptyState
                icon="✅"
                title="No tasks yet"
                subtitle="Keep your operations on track — assign packing, restocking, follow-ups to your team and never miss a step."
                actionLabel="+ Create your first task"
                onAction={()=>openAdd()}
                columns={["Task","Priority","Status","Assignee","Due Date"]}
                exampleRows={[
                  ["Pack order #1042","High","To Do","Rahul","Today"],
                  ["Restock AT_03 belt","Medium","In Progress","Priya","Tomorrow"],
                  ["Reply to Myntra query","Low","Done","You","Yesterday"],
                ]}
              />
            </div>
          ) : (
            <div style={{ ...S.card, overflow:"hidden" }}>
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Task","Category","Priority","Status","Assignee","Due Date","Progress","Actions"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {filtered.map((t,i)=>{
                    const due     = daysUntil(t.dueDate);
                    const overdue = isOverdue(t);
                    const checks  = t.checklist||[];
                    const done    = checks.filter(c=>c.done).length;
                    return (
                      <tr key={t._id} style={{ background:overdue?"#fff5f5":i%2?"#fafafa":"#fff" }}
                        onMouseEnter={e=>e.currentTarget.style.background=overdue?"#fef2f2":"#fff8f2"}
                        onMouseLeave={e=>e.currentTarget.style.background=overdue?"#fff5f5":i%2?"#fafafa":"#fff"}>
                        <td style={{ ...S.td, maxWidth:220 }}>
                          <div style={{ fontWeight:500, fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.title}</div>
                          {t.description && <div style={{ fontSize:10, color:"#9ca3af", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{t.description}</div>}
                          {overdue && <span style={{ fontSize:9, color:"#dc2626", fontWeight:700 }}>⚠ OVERDUE</span>}
                        </td>
                        <td style={S.td}><span style={{ fontSize:10, background:"#f3f4f6", padding:"2px 7px", borderRadius:8 }}>{t.category||"—"}</span></td>
                        <td style={S.td}><PriorityBadge id={t.priority}/></td>
                        <td style={S.td}>
                          <select value={t.status||"todo"} onChange={e=>changeStatus(t._id,e.target.value)}
                            style={{ fontSize:10, padding:"2px 6px", borderRadius:6, border:"1px solid #e5e7eb", background:STATUSES.find(s=>s.id===t.status)?.bg||"#f9fafb", color:STATUSES.find(s=>s.id===t.status)?.color||"#6b7280", fontWeight:600, cursor:"pointer", fontFamily:"inherit" }}>
                            {STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                          </select>
                        </td>
                        <td style={S.td}>{t.assignee||<span style={{ color:"#9ca3af" }}>Unassigned</span>}</td>
                        <td style={{ ...S.td, color:overdue?"#dc2626":due<=1?"#d97706":"#374151", fontSize:11, fontWeight:overdue?700:400 }}>
                          {t.dueDate ? (due===0?"Today":due===1?"Tomorrow":due<0?Math.abs(due)+" days ago":due+" days left") : "—"}
                        </td>
                        <td style={S.td}>
                          {checks.length>0 ? (
                            <div>
                              <div style={{ height:4, background:"#e5e7eb", borderRadius:2, width:60, overflow:"hidden", marginBottom:2 }}>
                                <div style={{ height:4, background:"#15803d", width:(done/checks.length*100)+"%", borderRadius:2 }}/>
                              </div>
                              <div style={{ fontSize:9, color:"#9ca3af" }}>{done}/{checks.length}</div>
                            </div>
                          ) : "—"}
                        </td>
                        <td style={S.td}>
                          <div style={{ display:"flex", gap:4 }}>
                            <button style={{ ...S.btn("#2563eb"), padding:"3px 8px", fontSize:10 }} onClick={()=>openEdit(t)}>Edit</button>
                            <button style={{ ...S.btnOut, padding:"3px 8px", fontSize:10, color:"#dc2626" }} onClick={()=>deleteTask(t._id)}>Del</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ══ KANBAN ══ */}
      {tab === "kanban" && (
        <div className="kanban-board" style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
          {kanbanCols.map(col=>(
            <div key={col.id} style={{ background:"#f9fafb", borderRadius:10, padding:10, minHeight:300 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
                <span style={{ fontWeight:700, fontSize:12, color:col.color }}>{col.label}</span>
                <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <span style={{ background:col.bg, color:col.color, fontSize:10, padding:"1px 7px", borderRadius:10, fontWeight:700 }}>{col.tasks.length}</span>
                  <button style={{ ...S.btn(col.color), padding:"2px 7px", fontSize:10 }} onClick={()=>openAdd({status:col.id})}>+</button>
                </div>
              </div>
              {col.tasks.map(t=>{
                const due     = daysUntil(t.dueDate);
                const overdue = isOverdue(t);
                return (
                  <div key={t._id} style={{ background:"#fff", borderRadius:8, border:"1px solid "+(overdue?"#fecaca":"#e5e7eb"), padding:"10px 12px", marginBottom:8, cursor:"pointer", borderLeft:"3px solid "+(PRIORITIES.find(p=>p.id===t.priority)?.color||"#e5e7eb") }}
                    onClick={()=>openEdit(t)}>
                    <div style={{ fontWeight:500, fontSize:12, marginBottom:5, lineHeight:1.3 }}>{t.title}</div>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <PriorityBadge id={t.priority}/>
                      {t.dueDate && <span style={{ fontSize:9, color:overdue?"#dc2626":due<=1?"#d97706":"#9ca3af", fontWeight:overdue?700:400 }}>
                        📅 {due===0?"Today":due===1?"Tomorrow":due<0?Math.abs(due)+"d late":due+"d left"}
                      </span>}
                    </div>
                    {t.assignee && <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>👤 {t.assignee}</div>}
                    {(t.checklist||[]).length>0 && (
                      <div style={{ fontSize:10, color:"#9ca3af", marginTop:4 }}>
                        ✓ {t.checklist.filter(c=>c.done).length}/{t.checklist.length} done
                      </div>
                    )}
                  </div>
                );
              })}
              {col.tasks.length===0 && <div style={{ textAlign:"center", color:"#9ca3af", fontSize:11, padding:"20px 0" }}>No tasks</div>}
            </div>
          ))}
        </div>
      )}

      {/* ══ TEAM ══ */}
      {tab === "team" && (
        <div>
          <div style={{ ...S.card, overflow:"hidden" }}>
            <div style={S.hdr}>
              <span>👤 Team Members</span>
              <button style={S.btn()} onClick={()=>window.location.href="#"}>+ Invite Member</button>
            </div>
            {members.length===0 ? (
              <div style={{ padding:40, textAlign:"center", color:"#9ca3af" }}>
                <div style={{ fontSize:36, marginBottom:10 }}>👥</div>
                <div style={{ marginBottom:8 }}>No team members yet</div>
                <div style={{ fontSize:11 }}>Go to Settings → Team to invite members</div>
              </div>
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse" }}>
                <thead><tr>{["Member","Email","Role","Tasks Assigned","Status"].map(h=><th key={h} style={S.th}>{h}</th>)}</tr></thead>
                <tbody>
                  {members.map((m,i)=>{
                    const assigned = tasks.filter(t=>t.assignee===m.name).length;
                    const overdue  = tasks.filter(t=>t.assignee===m.name&&isOverdue(t)).length;
                    return (
                      <tr key={m._id} style={{ background:i%2?"#fafafa":"#fff" }}>
                        <td style={S.td}>
                          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:32, height:32, borderRadius:"50%", background:"#F47920"+"22", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:700, color:"#F47920", fontSize:14 }}>
                              {(m.name||"?").charAt(0).toUpperCase()}
                            </div>
                            <span style={{ fontWeight:600, fontSize:12 }}>{m.name}</span>
                          </div>
                        </td>
                        <td style={{ ...S.td, color:"#6b7280" }}>{m.email}</td>
                        <td style={S.td}><span style={{ fontSize:10, background:"#eff6ff", color:"#2563eb", padding:"2px 8px", borderRadius:8, fontWeight:600 }}>{m.role||"Staff"}</span></td>
                        <td style={S.td}>
                          <div style={{ fontSize:12 }}>{assigned} tasks</div>
                          {overdue>0 && <div style={{ fontSize:10, color:"#dc2626" }}>{overdue} overdue</div>}
                        </td>
                        <td style={S.td}><span style={{ ...S.badge("#15803d","#f0fdf4") }}>Active</span></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* Team task workload */}
          {members.length>0 && (
            <div style={S.card}>
              <div style={S.hdr}><span>📊 Team Workload</span></div>
              <div style={{ padding:14 }}>
                {members.map(m=>{
                  const total    = tasks.filter(t=>t.assignee===m.name).length;
                  const done     = tasks.filter(t=>t.assignee===m.name&&t.status==="done").length;
                  const pct      = total>0?Math.round(done/total*100):0;
                  return (
                    <div key={m._id} style={{ marginBottom:12 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:12, marginBottom:4 }}>
                        <span style={{ fontWeight:600 }}>{m.name}</span>
                        <span style={{ color:"#9ca3af" }}>{done}/{total} done ({pct}%)</span>
                      </div>
                      <div style={{ height:6, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                        <div style={{ height:6, background:"#15803d", width:pct+"%", borderRadius:3, transition:"width .4s" }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══ AUTOMATION ══ */}
      {tab === "automation" && (
        <div>
          {showRuleForm && (
            <div style={{ ...S.card, padding:16, marginBottom:14 }}>
              <div style={{ fontWeight:700, fontSize:13, marginBottom:12 }}>⚡ New Automation Rule</div>
              <div className="task-report-grid" className="wa-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10, marginBottom:10 }}>
                <div style={{ gridColumn:"1/-1" }}>
                  <label style={S.label}>Rule Name *</label>
                  <input style={{ ...S.input, width:"100%" }} placeholder="e.g. Low stock alert" value={ruleForm.name} onChange={e=>setRuleForm(f=>({...f,name:e.target.value}))}/>
                </div>
                <div>
                  <label style={S.label}>When (Trigger)</label>
                  <select style={{ ...S.input, width:"100%" }} value={ruleForm.trigger} onChange={e=>setRuleForm(f=>({...f,trigger:e.target.value}))}>
                    {RULE_TRIGGERS.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Then (Action)</label>
                  <select style={{ ...S.input, width:"100%" }} value={ruleForm.action} onChange={e=>setRuleForm(f=>({...f,action:e.target.value}))}>
                    {RULE_ACTIONS.map(a=><option key={a.id} value={a.id}>{a.label}</option>)}
                  </select>
                </div>
                {ruleForm.action==="create_task" && (
                  <div style={{ gridColumn:"1/-1" }}>
                    <label style={S.label}>Task Title to Create</label>
                    <input style={{ ...S.input, width:"100%" }} placeholder="e.g. Restock {product_name}" value={ruleForm.taskTitle} onChange={e=>setRuleForm(f=>({...f,taskTitle:e.target.value}))}/>
                  </div>
                )}
                {(ruleForm.action==="whatsapp_alert"||ruleForm.action==="email_alert") && (
                  <div style={{ gridColumn:"1/-1" }}>
                    <label style={S.label}>WhatsApp/Phone Number</label>
                    <input style={{ ...S.input, width:"100%" }} placeholder="91XXXXXXXXXX" value={ruleForm.whatsappNo} onChange={e=>setRuleForm(f=>({...f,whatsappNo:e.target.value}))}/>
                  </div>
                )}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button style={S.btn()} onClick={saveRule}>Save Rule</button>
                <button style={S.btnOut} onClick={()=>setShowRuleForm(false)}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div>
              <div style={{ fontWeight:600, fontSize:14 }}>⚡ Automation Rules</div>
              <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>Auto-create tasks or send alerts based on triggers</div>
            </div>
            <button style={S.btn()} onClick={()=>setShowRuleForm(true)}>+ New Rule</button>
          </div>

          {rules.length===0 ? (
            <div style={{ ...S.card, padding:40, textAlign:"center", color:"#9ca3af" }}>
              <div style={{ fontSize:36, marginBottom:10 }}>⚡</div>
              <div style={{ marginBottom:12 }}>No automation rules yet</div>
              <div style={{ fontSize:11, marginBottom:16 }}>Create rules to auto-generate tasks or send alerts</div>
              <button style={S.btn()} onClick={()=>setShowRuleForm(true)}>Create First Rule</button>
            </div>
          ) : (
            <div style={S.card}>
              {rules.map((r,i)=>{
                const trigger = RULE_TRIGGERS.find(t=>t.id===r.trigger);
                const action  = RULE_ACTIONS.find(a=>a.id===r.action);
                return (
                  <div key={r.id} style={{ padding:"14px 16px", borderBottom:"1px solid #f3f4f6", display:"flex", alignItems:"center", gap:14 }}>
                    <div style={{ width:36, height:36, borderRadius:8, background:r.active?"#f0fdf4":"#f9fafb", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>
                      {r.active?"⚡":"⏸️"}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:600, fontSize:13, marginBottom:4 }}>{r.name}</div>
                      <div style={{ fontSize:11, color:"#6b7280" }}>
                        <span style={{ background:"#eff6ff", color:"#2563eb", padding:"1px 7px", borderRadius:8, marginRight:6 }}>{trigger?.label||r.trigger}</span>
                        →
                        <span style={{ background:"#fff8f2", color:"#F47920", padding:"1px 7px", borderRadius:8, marginLeft:6 }}>{action?.label||r.action}</span>
                        {r.taskTitle && <span style={{ color:"#9ca3af", marginLeft:6 }}>"{r.taskTitle}"</span>}
                      </div>
                    </div>
                    <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                      <button style={{ ...S.btn(r.active?"#15803d":"#9ca3af"), padding:"4px 10px", fontSize:10 }} onClick={()=>toggleRule(r.id)}>
                        {r.active?"Active":"Paused"}
                      </button>
                      <button style={{ ...S.btnOut, padding:"4px 10px", fontSize:10, color:"#dc2626" }} onClick={()=>deleteRule(r.id)}>Del</button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ══ WHATSAPP ALERTS ══ */}
      {tab === "whatsapp" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={S.card}>
              <div style={S.hdr}><span>📱 WhatsApp Setup</span></div>
              <div style={{ padding:16 }}>
                <div style={{ background:"#f0fdf4", borderRadius:8, padding:"10px 14px", fontSize:12, color:"#15803d", marginBottom:14 }}>
                  💡 Send instant WhatsApp alerts for low stock, pending orders, and daily summaries — directly to your phone or team
                </div>
                <label style={S.label}>Your WhatsApp Number (with country code)</label>
                <div style={{ display:"flex", gap:8, marginBottom:14 }}>
                  <input style={{ ...S.input, flex:1 }} placeholder="919876543210" value={waNumber} onChange={e=>setWaNumber(e.target.value)}/>
                  <button style={S.btn(waSaved?"#15803d":undefined)} onClick={saveWaNumber}>{waSaved?"Saved ✓":"Save"}</button>
                </div>

                <div style={{ fontWeight:600, fontSize:12, marginBottom:10 }}>Quick Alert Buttons</div>
                {[
                  { label:"📦 Low Stock Alert", msg:`3June Alert: You have ${products.filter(p=>Object.values(p.inventory||{}).reduce((a,b)=>a+b,0)<=(p.lowAlert||5)).length} products with low stock. Check inventory now.` },
                  { label:"⚠️ Overdue Tasks", msg:`3June Alert: You have ${overdueTasks.length} overdue tasks. Please review immediately.` },
                  { label:"📊 Daily Summary", msg:`3June Daily Summary:\n✅ Tasks done today: ${doneTasks.length}\n⚠️ Overdue: ${overdueTasks.length}\n🔴 Urgent: ${urgentTasks.length}\nTotal active tasks: ${totalTasks-doneTasks.length}` },
                  { label:"🛒 Pending Orders", msg:`3June Alert: You have pending orders waiting to be fulfilled. Log in to process them.` },
                ].map(alert=>(
                  <a key={alert.label} href={`https://wa.me/${waNumber||"91XXXXXXXXXX"}?text=${encodeURIComponent(alert.msg)}`} target="_blank" rel="noreferrer"
                    style={{ display:"block", padding:"10px 14px", borderRadius:8, border:"1px solid #e5e7eb", marginBottom:8, textDecoration:"none", color:"#374151", fontSize:12, fontWeight:500, background:"#fff" }}>
                    {alert.label} →
                  </a>
                ))}
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>📋 Alert Templates</span></div>
              <div style={{ padding:16 }}>
                {[
                  { type:"Low Stock", msg:"🔴 *Low Stock Alert*\nProduct: {product_name}\nCurrent Stock: {stock} units\nAction: Reorder immediately\n\n_Sent by 3June_" },
                  { type:"New Order", msg:"🛒 *New Order Received*\nOrder: {order_id}\nCustomer: {customer_name}\nAmount: Rs.{amount}\nPlatform: {platform}\n\n_Sent by 3June_" },
                  { type:"Daily Summary", msg:"📊 *Daily Operations Summary*\nDate: {date}\n\nTasks:\n✅ Completed: {done}\n⚠️ Overdue: {overdue}\n🔴 Urgent: {urgent}\n\n_Sent by 3June_" },
                  { type:"Task Reminder", msg:"⏰ *Task Reminder*\nTask: {task_title}\nDue: {due_date}\nAssigned to: {assignee}\n\n_Sent by 3June_" },
                ].map(t=>(
                  <div key={t.type} style={{ marginBottom:12 }}>
                    <div style={{ fontWeight:600, fontSize:12, marginBottom:4, color:"#25d366" }}>💬 {t.type}</div>
                    <div style={{ background:"#f9fafb", borderRadius:8, padding:"8px 12px", fontSize:11, color:"#6b7280", fontFamily:"monospace", lineHeight:1.6 }}>
                      {t.msg}
                    </div>
                    <button style={{ ...S.btnOut, marginTop:5, fontSize:10 }} onClick={()=>{navigator.clipboard.writeText(t.msg); toast("Template copied!","success");}}>Copy Template</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ REPORTS ══ */}
      {tab === "reports" && (
        <div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            <div style={S.card}>
              <div style={S.hdr}><span>📊 Task Completion Report</span></div>
              <div style={{ padding:14 }}>
                {[
                  { label:"Total Tasks",   value:totalTasks,                           color:"#2563eb" },
                  { label:"Done",          value:doneTasks.length,                     color:"#15803d" },
                  { label:"In Progress",   value:tasks.filter(t=>t.status==="in_progress").length, color:"#2563eb" },
                  { label:"To Do",         value:tasks.filter(t=>t.status==="todo").length,        color:"#6b7280" },
                  { label:"Overdue",       value:overdueTasks.length,                  color:"#dc2626" },
                  { label:"Completion %",  value:totalTasks>0?Math.round(doneTasks.length/totalTasks*100)+"%":"0%", color:"#F47920" },
                ].map(r=>(
                  <div key={r.label} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid #f9fafb" }}>
                    <span style={{ fontSize:12, color:"#6b7280" }}>{r.label}</span>
                    <span style={{ fontWeight:700, color:r.color }}>{r.value}</span>
                  </div>
                ))}
                <div style={{ marginTop:14 }}>
                  <div style={{ height:8, background:"#f3f4f6", borderRadius:4, overflow:"hidden" }}>
                    <div style={{ height:8, background:"#15803d", width:(totalTasks>0?doneTasks.length/totalTasks*100:0)+"%", borderRadius:4, transition:"width .5s" }}/>
                  </div>
                  <div style={{ fontSize:10, color:"#9ca3af", marginTop:4, textAlign:"center" }}>
                    {totalTasks>0?Math.round(doneTasks.length/totalTasks*100):0}% completion rate
                  </div>
                </div>
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>🏷️ Tasks by Category</span></div>
              <div style={{ padding:14 }}>
                {CATEGORIES.map(cat=>{
                  const catTasks = tasks.filter(t=>t.category===cat);
                  const catDone  = catTasks.filter(t=>t.status==="done").length;
                  if (!catTasks.length) return null;
                  return (
                    <div key={cat} style={{ marginBottom:10 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:3 }}>
                        <span style={{ fontWeight:600 }}>{cat}</span>
                        <span style={{ color:"#9ca3af" }}>{catDone}/{catTasks.length}</span>
                      </div>
                      <div style={{ height:5, background:"#f3f4f6", borderRadius:3, overflow:"hidden" }}>
                        <div style={{ height:5, background:"#F47920", width:(catTasks.length>0?catDone/catTasks.length*100:0)+"%", borderRadius:3 }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>🔴 Priority Breakdown</span></div>
              <div style={{ padding:14, display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                {PRIORITIES.map(p=>{
                  const count = tasks.filter(t=>t.priority===p.id).length;
                  const done  = tasks.filter(t=>t.priority===p.id&&t.status==="done").length;
                  return (
                    <div key={p.id} style={{ background:p.bg, borderRadius:8, padding:"12px 14px", textAlign:"center" }}>
                      <div style={{ fontSize:20, marginBottom:4 }}>{p.label.split(" ")[0]}</div>
                      <div style={{ fontWeight:700, fontSize:18, color:p.color }}>{count}</div>
                      <div style={{ fontSize:10, color:p.color, opacity:0.8 }}>{p.label.split(" ").slice(1).join(" ")}</div>
                      <div style={{ fontSize:9, color:"#9ca3af", marginTop:2 }}>{done} done</div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div style={S.card}>
              <div style={S.hdr}><span>⏰ Upcoming Deadlines</span></div>
              <div style={{ padding:"4px 0", maxHeight:220, overflowY:"auto" }}>
                {tasks.filter(t=>t.dueDate&&t.status!=="done").sort((a,b)=>new Date(a.dueDate)-new Date(b.dueDate)).slice(0,8).map(t=>{
                  const due     = daysUntil(t.dueDate);
                  const overdue = due < 0;
                  return (
                    <div key={t._id} style={{ padding:"8px 16px", borderBottom:"1px solid #f9fafb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div>
                        <div style={{ fontSize:12, fontWeight:500 }}>{t.title}</div>
                        <PriorityBadge id={t.priority}/>
                      </div>
                      <span style={{ fontSize:11, fontWeight:600, color:overdue?"#dc2626":due<=1?"#d97706":"#6b7280", flexShrink:0, marginLeft:10 }}>
                        {due===0?"Today":due===1?"Tomorrow":overdue?Math.abs(due)+"d late":due+"d left"}
                      </span>
                    </div>
                  );
                })}
                {tasks.filter(t=>t.dueDate&&t.status!=="done").length===0 && (
                  <div style={{ padding:24, textAlign:"center", color:"#9ca3af", fontSize:12 }}>No upcoming deadlines</div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ TASK MODAL ══ */}
      {showModal && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.4)", zIndex:9999, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}
          onClick={e=>e.target===e.currentTarget&&setShowModal(false)}>
          <div style={{ background:"#fff", borderRadius:12, width:"100%", maxWidth:540, maxHeight:"90vh", overflow:"auto", boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ padding:"14px 20px", borderBottom:"1px solid #e5e7eb", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontWeight:700, fontSize:14 }}>{editTask?"Edit Task":"New Task"}</span>
              <button onClick={()=>setShowModal(false)} style={{ background:"none", border:"none", fontSize:20, cursor:"pointer", color:"#9ca3af" }}>×</button>
            </div>
            <div style={{ padding:20, display:"flex", flexDirection:"column", gap:12 }}>
              <div>
                <label style={S.label}>Task Title *</label>
                <input style={{ ...S.input, width:"100%" }} placeholder="e.g. Restock black belts on Amazon" value={form.title} onChange={e=>setF("title",e.target.value)}/>
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                <div>
                  <label style={S.label}>Category</label>
                  <select style={{ ...S.input, width:"100%" }} value={form.category} onChange={e=>setF("category",e.target.value)}>
                    {CATEGORIES.map(c=><option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Priority</label>
                  <select style={{ ...S.input, width:"100%" }} value={form.priority} onChange={e=>setF("priority",e.target.value)}>
                    {PRIORITIES.map(p=><option key={p.id} value={p.id}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Status</label>
                  <select style={{ ...S.input, width:"100%" }} value={form.status} onChange={e=>setF("status",e.target.value)}>
                    {STATUSES.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={S.label}>Due Date</label>
                  <input type="date" style={{ ...S.input, width:"100%" }} value={form.dueDate} onChange={e=>setF("dueDate",e.target.value)}/>
                </div>
                <div>
                  <label style={S.label}>Assign To</label>
                  <select style={{ ...S.input, width:"100%" }} value={form.assignee} onChange={e=>setF("assignee",e.target.value)}>
                    <option value="">Unassigned</option>
                    {members.map(m=><option key={m._id} value={m.name}>{m.name}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label style={S.label}>Description</label>
                <textarea style={{ ...S.input, width:"100%", height:70, resize:"vertical" }} placeholder="Task details…" value={form.description} onChange={e=>setF("description",e.target.value)}/>
              </div>
              {/* Checklist */}
              <div>
                <label style={S.label}>Checklist</label>
                <div style={{ display:"flex", gap:8, marginBottom:8 }}>
                  <input style={{ ...S.input, flex:1 }} placeholder="Add checklist item…" value={checkInput} onChange={e=>setCheckInput(e.target.value)}
                    onKeyDown={e=>{ if(e.key==="Enter"&&checkInput.trim()){setF("checklist",[...form.checklist,{text:checkInput.trim(),done:false}]);setCheckInput(""); }}}/>
                  <button style={S.btn()} onClick={()=>{ if(checkInput.trim()){setF("checklist",[...form.checklist,{text:checkInput.trim(),done:false}]);setCheckInput("");}}} >Add</button>
                </div>
                {(form.checklist||[]).map((item,i)=>(
                  <div key={i} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:5 }}>
                    <input type="checkbox" checked={item.done} onChange={()=>{ const cl=[...form.checklist]; cl[i]={...cl[i],done:!cl[i].done}; setF("checklist",cl); }}/>
                    <span style={{ flex:1, fontSize:12, textDecoration:item.done?"line-through":"none", color:item.done?"#9ca3af":"#374151" }}>{item.text}</span>
                    <button style={{ background:"none", border:"none", color:"#9ca3af", cursor:"pointer" }} onClick={()=>setF("checklist",form.checklist.filter((_,j)=>j!==i))}>×</button>
                  </div>
                ))}
              </div>
            </div>
            <div style={{ padding:"14px 20px", borderTop:"1px solid #e5e7eb", display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button style={S.btnOut} onClick={()=>setShowModal(false)}>Cancel</button>
              <button style={S.btn()} onClick={saveTask}>{editTask?"Update Task":"Create Task"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
